import React, { type JSX } from 'react';

import { useConfirm } from '@fiji/common/src/hooks';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useEditScheduleStatusMutation } from '@fiji/common/src/features/schedule/scheduleApi';
import { useAppDispatch } from '@fiji/common';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { t } from 'i18next';
import { CustomTransComponent } from 'components';

// Pure module-level helpers — keep decision points out of the component

const getScheduleStatus = (enabled: boolean): string => (enabled ? t('SCHEDULING:DISABLE') : t('SCHEDULING:ENABLE'));

const getSuccessMessage = (name: string, enabled: boolean): string =>
    `${name} ${enabled ? t('COMMON:DISABLED') : t('COMMON:ENABLED')} ${t('SCHEDULING:SUCCESSFULLY')}`;

const formatNextRun = (nextRun: string | undefined): string => (nextRun ? moment(nextRun).format('LL') : '-');

const renderActions = (actions: any[]): JSX.Element => {
    if (actions.length > 2) {
        return (
            <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                {`${actions[0]?.name}, ${actions[1]?.name} and ${actions.length - 2} others`}
            </Typography>
        );
    }
    return (
        <>
            {actions.map((item: any) => (
                <Typography key={item?.id} variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                    {item?.name ?? '-'}
                </Typography>
            ))}
        </>
    );
};

export const DisableScheduleModal = React.forwardRef((_, ref): JSX.Element => {
    const dispatch = useAppDispatch();
    const [editSchedule, { isLoading }] = useEditScheduleStatusMutation();
    const handleNextSchedule = async (): Promise<void> => {
        const { error }: any = await editSchedule({
            scheduleId: data?.id,
            params: {
                status: 'SKIPNEXTTRIGGER',
            },
        });
        if (!error) {
            dispatch(setMessageContent({ isOpen: true, message: t('SCHEDULING:SKIPPED_NEXT_SCHEDULE') }));
            onCancel(true);
        }
    };

    const handleSubmit = async (): Promise<void> => {
        const enabled = Boolean(data?.enabled);
        const { error }: any = await editSchedule({
            scheduleId: data?.id,
            params: { status: getScheduleStatus(enabled) },
        });
        if (!error) {
            dispatch(setMessageContent({ isOpen: true, message: getSuccessMessage(name, enabled) }));
            onCancel(true);
        }
    };
    const { data, isVisible, onCancel, onClick, onConfirm } = useConfirm(handleSubmit);
    const { name = '-', deviceCount = '-', actions = [], frequency = '-', nextRun } = data ?? {};

    React.useImperativeHandle(ref, () => ({
        handleDisableModal: onClick,
    }));
    return (
        <Dialog onClose={(): void => onCancel(true)} open={isVisible}>
            <DialogTitle>
                <CustomTransComponent translationKey={'SCHEDULING:DISABLE_SCHEDULE'} />
            </DialogTitle>
            <DialogContent sx={{ width: '444px' }}>
                <Typography variant="body1">
                    <CustomTransComponent translationKey={'SCHEDULING:CHOOSE_ALL_EVENTS'} />
                </Typography>

                <Stack mt={4}>
                    <Typography variant="subtitle2" fontWeight={'600'}>
                        {name}
                    </Typography>
                    <Stack spacing={1} mt={1}>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="body2">
                                <CustomTransComponent translationKey={'COMMON:DEVICES'} />
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                                {deviceCount}
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="body2">
                                <CustomTransComponent translationKey={'COMMON:ACTION'} />
                            </Typography>
                            {renderActions(actions)}
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="body2">
                                <CustomTransComponent translationKey={'COMMON:FREQUENCY'} />
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                                {frequency}
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="body2">
                                <CustomTransComponent translationKey={'COMMON:EXCEPTION'} />
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                                <CustomTransComponent translationKey={'SCHEDULING:HOLIDAY'} />
                            </Typography>
                        </Stack>
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="body2">
                                <CustomTransComponent translationKey={'SCHEDULING:NEXT_SCHEDULE'} />
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                                {formatNextRun(nextRun)}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions className="border-top-1">
                <Stack direction={'row'} alignItems={'center'} spacing={3}>
                    <Button variant="outlined" color="primary" onClick={(): void => onCancel(true)}>
                        <CustomTransComponent translationKey={'COMMON:CLOSE'} />
                    </Button>
                    <Button variant="outlined" color="primary" disabled={isLoading} onClick={handleNextSchedule}>
                        <CustomTransComponent translationKey={'SCHEDULING:NEXT_SCHEDULE'} />
                    </Button>
                    <Button variant="contained" color="primary" disabled={isLoading} onClick={onConfirm}>
                        <CustomTransComponent translationKey={'COMMON:ALL_EVENTS'} />
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
});
